// @flow strict
/* eslint-disable react/no-danger */
import * as React from 'react';
import styledImport from 'styled-components';
const styled = styledImport.default || styledImport;
import { Box, Flex } from '../../primitives/Essentials/index.js';
import IntlContext from '../../services/intl/context.js';
import MagazineContext from '../../services/magazine/context.js';
import type { MagazinePostSummary } from '../../services/magazine/context.js';
import { container } from '../../services/styleUtils/index.js';
import Footer from '../../components/Footer/index.js';
import MoreLinks from '../../components/MoreLinks/index.js';
import Navbar from '../../components/Navbar/index.js';
import Breadcrumbs from '../../components/Breadcrumbs/index.js';
import NotFoundPage from '../NotFoundPage/index.js';
import { SECTION_LAYOUT } from '../../consts/layout.js';

const RELATED = {
  en: 'More from the Magazine',
  de: 'Mehr aus dem Magazin',
  it: 'Altro dal Magazine',
};

const StyledContainer = styled.div`
  position: relative;
`;

const StyledContainerBox = styled(Box)`
  ${container()};
`;

const ArticleShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 300px;
  gap: 48px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const Article = styled.article`
  min-width: 0;
`;

const Category = styled.div`
  margin-bottom: 12px;
  color: ${({ theme }) => theme.secondary};
  font-size: 13px;
  font-weight: 900;
  text-transform: uppercase;
`;

const Title = styled.h1`
  max-width: 860px;
  margin: 0;
  color: ${({ theme }) => theme.section.title};
  font-size: 44px;
  font-weight: 900;
  line-height: 1.1;

  @media (max-width: 640px) {
    font-size: 32px;
  }
`;

const Excerpt = styled.p`
  max-width: 780px;
  margin: 20px 0 0;
  color: ${({ theme }) => theme.section.abstract};
  font-size: 20px;
  line-height: 1.5;
`;

const Byline = styled.div`
  margin-top: 18px;
  color: #777;
  font-size: 14px;
`;

const HeroImage = styled.img`
  display: block;
  width: 100%;
  max-height: 520px;
  margin-top: 34px;
  border-radius: 8px;
  object-fit: cover;
`;

const Body = styled.div`
  margin-top: 36px;
  color: #242124;
  font-size: 18px;
  line-height: 1.72;

  p {
    margin: 0 0 22px;
  }

  a {
    color: ${({ theme }) => theme.secondary};
    font-weight: 700;
  }

  h2,
  h3,
  h4 {
    margin: 42px 0 16px;
    color: ${({ theme }) => theme.section.title};
    font-weight: 900;
    line-height: 1.2;
  }

  h2 {
    font-size: 30px;
  }

  h3 {
    font-size: 24px;
  }

  ul,
  ol {
    margin: 0 0 24px;
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  img {
    display: block;
    max-width: 100%;
    height: auto;
    border-radius: 6px;
  }

  figure {
    max-width: 100%;
    margin: 32px 0;
  }

  figcaption,
  .wp-caption-text {
    margin-top: 8px;
    color: #777;
    font-size: 13px;
    line-height: 1.4;
    text-align: center;
  }

  table {
    display: block;
    width: 100%;
    max-width: 100%;
    overflow-x: auto;
    border-collapse: collapse;
    font-size: 15px;
    line-height: 1.35;
  }

  td,
  th {
    padding: 9px 11px;
    border: 1px solid #ddd;
    vertical-align: top;
  }

  .tl-wpdatatable {
    max-width: 100%;
    margin: 32px 0 38px;
  }

  .tl-wpdatatable__title {
    margin: 0 0 12px;
    font-size: 22px;
  }

  .tl-wpdatatable__scroll {
    max-width: 100%;
    overflow-x: auto;
    border: 1px solid #ded8dc;
    border-radius: 8px;
    -webkit-overflow-scrolling: touch;
  }

  .tl-wpdatatable__table {
    display: table;
    min-width: 780px;
    overflow: visible;
    border-collapse: collapse;
    background: #fff;
    font-size: 14px;
    line-height: 1.35;
  }

  .tl-wpdatatable__table th,
  .tl-wpdatatable__table td {
    padding: 10px 12px;
    border: 1px solid #ded8dc;
    text-align: center;
    vertical-align: middle;
  }

  .tl-wpdatatable__table thead th {
    background: #342c33;
    color: #fff;
    font-size: 13px;
    font-weight: 900;
  }

  .tl-wpdatatable__cell {
    background: #fff;
    color: #2b252c;
  }

  .tl-wpdatatable__cell--row-header {
    min-width: 170px;
    text-align: left;
  }

  .tl-wpdatatable__cell--emphasis {
    font-weight: 900;
  }

  .tl-wpdatatable__cell--yes {
    background: #6abc10;
    color: #fff;
    font-weight: 900;
  }

  .tl-wpdatatable__cell--free {
    background: #a3d86a;
    color: #fff;
    font-weight: 900;
  }

  .tl-wpdatatable__cell--optional,
  .tl-wpdatatable__cell--discount {
    background: #ffb300;
    color: #fff;
    font-weight: 900;
  }

  .tl-wpdatatable__cell--no {
    background: #f5f2f4;
    color: #6f6870;
  }

  .tl-wpdatatable__cell a {
    white-space: nowrap;
  }

  @media (max-width: 640px) {
    .tl-wpdatatable__table {
      font-size: 13px;
    }

    .tl-wpdatatable__table th,
    .tl-wpdatatable__table td {
      padding: 8px 10px;
    }
  }

  .wp-block-gallery {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;

    @media (max-width: 620px) {
      grid-template-columns: 1fr;
    }
  }

  .wp-block-gallery figure {
    margin: 0;
  }
`;

const Sidebar = styled.aside`
  align-self: start;
  padding-top: 8px;

  @media (max-width: 980px) {
    padding-top: 0;
  }
`;

const SidebarTitle = styled.h2`
  margin: 0 0 16px;
  color: ${({ theme }) => theme.section.title};
  font-size: 20px;
  font-weight: 900;
`;

const RelatedLink = styled.a`
  display: block;
  padding: 14px 0;
  border-top: 1px solid #e6e2e4;
  color: ${({ theme }) => theme.section.title};
  font-size: 15px;
  font-weight: 900;
  line-height: 1.35;
  text-decoration: none;

  &:hover {
    color: ${({ theme }) => theme.secondary};
  }
`;

const formatDate = (language, date) =>
  date
    ? new Intl.DateTimeFormat(language, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }).format(new Date(date))
    : '';

const categoryLabel = post =>
  post.categories && post.categories[0] ? post.categories[0].name : 'Magazine';

const Related = ({
  posts,
  currentId,
}: {
  posts: MagazinePostSummary[],
  currentId: number,
}) =>
  posts
    .filter(post => post.id !== currentId)
    .slice(0, 5)
    .map(post => (
      <RelatedLink href={post.urlPath} key={post.id}>
        {post.title}
      </RelatedLink>
    ));

const MagazineArticlePage = () => {
  const { language, translate } = React.useContext(IntlContext);
  const { currentPost, recentPosts } = React.useContext(MagazineContext);

  if (!currentPost) {
    return <NotFoundPage language={language} />;
  }

  return (
    <>
      <StyledContainer>
        <Navbar showOptions={false} />
      </StyledContainer>
      <StyledContainerBox {...SECTION_LAYOUT} $mt={0}>
        <Flex $flexDirection="column">
          <Breadcrumbs
            items={[
              {
                name: translate('magazine'),
                url: `/${language}/magazine`,
              },
              {
                name: currentPost.title,
                url: currentPost.urlPath,
              },
            ]}
            activeIndex={1}
          />
          <ArticleShell>
            <Article>
              <Category>{categoryLabel(currentPost)}</Category>
              <Title>{currentPost.title}</Title>
              {currentPost.excerpt && <Excerpt>{currentPost.excerpt}</Excerpt>}
              <Byline>
                {currentPost.authorName}
                {currentPost.date ? ` | ${formatDate(language, currentPost.date)}` : ''}
              </Byline>
              {currentPost.featuredImage && (
                <HeroImage
                  src={currentPost.featuredImage.url}
                  alt={currentPost.featuredImage.alt || currentPost.title}
                />
              )}
              <Body dangerouslySetInnerHTML={{ __html: currentPost.contentHtml }} />
            </Article>
            <Sidebar>
              <SidebarTitle>{RELATED[language] || RELATED.en}</SidebarTitle>
              <Related posts={recentPosts} currentId={currentPost.id} />
            </Sidebar>
          </ArticleShell>
        </Flex>
      </StyledContainerBox>
      <MoreLinks attractionsLimit={10} destinationsLimit={12} hideTopics />
      <Footer showOptions={false} />
    </>
  );
};

export default MagazineArticlePage;
